import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM, SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { TITLE_PORTAL_SOURCE_LABEL } from 'constants/documents';
import { getConditionalArray } from 'utils/data';
import { checkIsTitlePortalUpload } from 'utils/documents';

export const InspectorTab = {
  AI_FIELDS: 'aiFields',
  LINKS: 'links',
  DETAILS: 'details',
};

/**
 * Backend enum: Interfirst.Skynet.Documents.DocumentExtractionRequestStatusType.
 * The document's embedded extractionRequest.status.type
 * (GET /api/documents/{id}?include=ExtractionRequest).
 */
export const EXTRACTION_REQUEST_STATUS_TYPE = {
  NOT_STARTED: 'NotStarted',
  SKIPPED_FOR_MANUAL: 'SkippedForManual',
  AI_PROCESSING: 'AiProcessing',
  READY_FOR_EXTRACTOR: 'ReadyForExtractor',
  EXTRACTOR_PROCESSING: 'ExtractorProcessing',
  PROCESSED: 'Processed',
  REJECTED: 'Rejected',
};

/** Backend enum: DocumentExtractionSessionStatus subtype names (session.status.type). */
export const EXTRACTION_SESSION_STATUS_TYPE = {
  ACTIVE: 'Active',
  SKIPPED: 'Skipped',
  REJECTED: 'Rejected',
  COMPLETED: 'Completed',
  ARCHIVED: 'Archived',
};

/** Backend enum: DocumentExtractionSessionMode subtype names (session.mode.type). */
export const EXTRACTION_SESSION_MODE_TYPE = {
  GEN_AI: 'GenAi',
};

/**
 * UI-facing panel states derived from the extraction request + its sessions.
 * ReadyForExtractor splits on session history: a completed GenAI session means the
 * AI already ran and a human review is pending (NEEDS_REVIEW); no completed GenAI
 * session means nothing ran yet and the document waits in the manual entry queue.
 */
export const EXTRACTION_PANEL_STATUS = {
  PROCESSED: 'processed',
  PROCESSING: 'processing',
  NEEDS_REVIEW: 'needsReview',
  AWAITING_MANUAL: 'awaitingManual',
  QUEUED: 'queued',
  FAILED: 'failed',
};

export const EXTRACTION_STATUS_CHIP_CONFIG = {
  [EXTRACTION_PANEL_STATUS.PROCESSED]: { label: 'Processed', variant: LabelVariant.GREEN },
  [EXTRACTION_PANEL_STATUS.PROCESSING]: {
    label: 'AI extraction running…',
    variant: LabelVariant.BLUE,
  },
  [EXTRACTION_PANEL_STATUS.NEEDS_REVIEW]: { label: 'Needs review', variant: LabelVariant.ORANGE },
  [EXTRACTION_PANEL_STATUS.AWAITING_MANUAL]: {
    label: 'Awaiting manual entry',
    variant: LabelVariant.ORANGE,
  },
  [EXTRACTION_PANEL_STATUS.QUEUED]: { label: 'Queued', variant: LabelVariant.GRAY },
  [EXTRACTION_PANEL_STATUS.FAILED]: { label: 'Extraction failed', variant: LabelVariant.RED },
};

export const EXTRACTION_HELPER_BANNER_TEXT = {
  [EXTRACTION_PANEL_STATUS.AWAITING_MANUAL]:
    'This company uses manual data entry — documents wait in the extraction queue.',
  [EXTRACTION_PANEL_STATUS.FAILED]:
    'Extraction was rejected — the document could not be processed automatically.',
};

/**
 * Base tab config without counts. DocumentInspector merges dynamic count values in
 * before passing to TabsCDS so that the count badges stay in sync with loaded data.
 */
export const INSPECTOR_TABS_CONFIG = [
  { label: 'AI Fields', value: InspectorTab.AI_FIELDS },
  { label: 'Links', value: InspectorTab.LINKS },
  { label: 'Details', value: InspectorTab.DETAILS },
];

export const INSPECTOR_PANE_WIDTH = 366;

/**
 * Extracts the uppercase file extension from a filename (e.g. "doc.pdf" → "PDF").
 * Returns null when the name is absent or has no extension.
 *
 * @param {string|null|undefined} fileName
 * @returns {string|null}
 */
export const getFileExtension = fileName => {
  if (_.isNil(fileName)) {
    return null;
  }

  const dotIndex = fileName.lastIndexOf('.');

  if (dotIndex === -1 || dotIndex === fileName.length - 1) {
    return null;
  }

  return fileName.slice(dotIndex + 1).toUpperCase();
};

/**
 * Builds the inspector header subtitle line.
 * Format: "TypeName · uploaded MMM D, YYYY" — uploader appended as "· F. LastName" if provided.
 *
 * @param {{ documentTypeName: string|null|undefined, createdDate: string|null|undefined, uploaderShortName: string|null|undefined }} params
 * @returns {string}
 */
export const getDocumentHeaderSubtitle = ({ documentTypeName, createdDate, uploaderShortName }) => {
  const formattedDate = _.isNil(createdDate)
    ? null
    : `uploaded ${dayjs(createdDate).format(SHORT_MONTH_DAY_YEAR_FORMAT)}`;

  const parts = [documentTypeName, formattedDate, uploaderShortName];

  return parts.filter(part => !_.isNil(part)).join(' · ');
};

/**
 * @param {{ document: object|undefined, documentTypeName: string|undefined, uploaderName: string|null|undefined }} params
 * @returns {{ id: string, label: string, value: string, isFullWidth: boolean }[]}
 */
export const getDetailRows = ({ document, documentTypeName, uploaderName = null }) => [
  { id: 'type', label: 'Type', value: documentTypeName ?? '—', isFullWidth: false },
  { id: 'name', label: 'Name', value: document?.name ?? '—', isFullWidth: true },
  {
    id: 'pages',
    label: 'Pages',
    value: document?.pagesCount != null ? String(document.pagesCount) : '—',
    isFullWidth: false,
  },
  {
    id: 'uploaded',
    label: 'Uploaded',
    value: document?.createdDate
      ? dayjs(document.createdDate).format(COMMON_DATETIME_FORMAT_FOR_TREE_ITEM)
      : '—',
    isFullWidth: false,
  },
  ...getConditionalArray(checkIsTitlePortalUpload(document?.UploadSource), () => [
    { id: 'source', label: 'Source', value: TITLE_PORTAL_SOURCE_LABEL, isFullWidth: false },
    { id: 'uploadedBy', label: 'Uploaded by', value: uploaderName ?? '—', isFullWidth: false },
  ]),
];
